import { Controller } from '@hotwired/stimulus';
import { csrfHeaders } from '../services/form.services';

type PushKeyResponse = {
    enabled: boolean;
    publicKey?: string | null;
    subscribed?: boolean;
    activeSubscriptionCount?: number;
};

const subscriptionSyncs = new Map<string, Promise<void>>();
const recentlySyncedSubscriptions = new Map<string, number>();
const subscriptionSyncTtlMs = 5 * 60 * 1000;

export class WebPushController extends Controller<HTMLElement> {
    static targets = ['status', 'toggleButton', 'toggleKnob', 'toggleLabel', 'unsupported'];

    declare readonly statusTarget: HTMLElement;
    declare readonly toggleButtonTarget: HTMLButtonElement;
    declare readonly toggleKnobTarget: HTMLElement;
    declare readonly toggleLabelTarget: HTMLElement;
    declare readonly unsupportedTarget: HTMLElement;
    declare readonly hasStatusTarget: boolean;
    declare readonly hasToggleButtonTarget: boolean;
    declare readonly hasToggleKnobTarget: boolean;
    declare readonly hasToggleLabelTarget: boolean;
    declare readonly hasUnsupportedTarget: boolean;

    private publicKey: string | null = null;
    private supported = false;
    private subscribed = false;

    connect(): void {
        this.supported = this.isSupported();
        if (!this.supported) {
            this.showUnsupported();
            return;
        }

        void this.refreshState();
    }

    async toggle(): Promise<void> {
        if (this.subscribed) {
            await this.disable();
            return;
        }
        await this.enable();
    }

    async enable(): Promise<void> {
        if (!this.supported || !this.publicKey) {
            return;
        }

        this.setBusy(true);
        try {
            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                this.setStatus('Push notifications are not enabled for this browser.');
                this.updateButtons(false);
                return;
            }

            const registration = await navigator.serviceWorker.ready;
            let subscription = await registration.pushManager.getSubscription();
            if (!subscription) {
                subscription = await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: this.urlBase64ToUint8Array(this.publicKey),
                });
            }
            await this.saveSubscription('/web-push/subscribe', subscription);
            this.setStatus('Push notifications are on for this device.');
            this.updateButtons(true);
        } catch (error) {
            console.error('web push enable failed', error);
            this.setStatus('Push notifications could not be enabled. Please try again.');
            this.updateButtons(false);
        } finally {
            this.setBusy(false);
        }
    }

    async disable(): Promise<void> {
        if (!this.supported) {
            return;
        }

        this.setBusy(true);
        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();
            if (subscription) {
                await this.saveSubscription('/web-push/unsubscribe', subscription);
                await subscription.unsubscribe();
            }
            this.setStatus('Push notifications are off for this device.');
            this.updateButtons(false);
        } catch (error) {
            console.error('web push disable failed', error);
            this.setStatus('Push notifications could not be disabled. Please try again.');
        } finally {
            this.setBusy(false);
        }
    }

    private async refreshState(): Promise<void> {
        try {
            const response = await fetch('/web-push/public-key', {
                headers: {
                    Accept: 'application/json',
                },
            });
            const payload = (await response.json()) as PushKeyResponse;
            this.publicKey = payload.publicKey || null;
            if (!payload.enabled || !this.publicKey) {
                this.setStatus('Push notifications are not configured yet.');
                this.updateButtons(false);
                this.setToggleDisabled(true);
                return;
            }

            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();
            if (Notification.permission === 'granted' && subscription) {
                await this.saveSubscription('/web-push/subscribe', subscription);
                this.setStatus('Push notifications are on for this device.');
                this.updateButtons(true);
            } else if (Notification.permission === 'granted') {
                await this.restoreSubscription(registration, Boolean(payload.subscribed));
            } else if (Notification.permission === 'denied') {
                this.setStatus('Push notifications are blocked in this browser.');
                this.updateButtons(false);
                this.setToggleDisabled(true);
            } else {
                this.setStatus('Get alerts when analysis finishes or buddies send updates.');
                this.updateButtons(false);
            }
        } catch (error) {
            console.error('web push state check failed', error);
            this.setStatus('Push notification status could not be checked.');
            this.updateButtons(false);
        }
    }

    private async restoreSubscription(registration: ServiceWorkerRegistration, hasServerSubscription: boolean): Promise<void> {
        if (!this.publicKey) {
            this.updateButtons(hasServerSubscription);
            return;
        }

        try {
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: this.urlBase64ToUint8Array(this.publicKey),
            });
            await this.saveSubscription('/web-push/subscribe', subscription);
            this.setStatus('Push notifications are on for this device.');
            this.updateButtons(true);
        } catch (error) {
            console.warn('web push subscription refresh failed', error);
            if (hasServerSubscription) {
                this.setStatus('Push notifications are on for another saved device.');
                this.updateButtons(true);
                return;
            }
            this.setStatus('Get alerts when analysis finishes or buddies send updates.');
            this.updateButtons(false);
        }
    }

    private async saveSubscription(url: string, subscription: PushSubscription): Promise<void> {
        const endpoint = subscription.endpoint;
        if (url === '/web-push/subscribe') {
            const lastSyncedAt = recentlySyncedSubscriptions.get(endpoint) || 0;
            if (Date.now() - lastSyncedAt < subscriptionSyncTtlMs) {
                return;
            }

            const activeSync = subscriptionSyncs.get(endpoint);
            if (activeSync) {
                return activeSync;
            }

            const sync = this.postSubscription(url, subscription)
                .then(() => {
                    recentlySyncedSubscriptions.set(endpoint, Date.now());
                })
                .finally(() => {
                    subscriptionSyncs.delete(endpoint);
                });
            subscriptionSyncs.set(endpoint, sync);
            return sync;
        }

        await this.postSubscription(url, subscription);
        recentlySyncedSubscriptions.delete(endpoint);
    }

    private async postSubscription(url: string, subscription: PushSubscription): Promise<void> {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                ...csrfHeaders(),
            },
            body: JSON.stringify(subscription.toJSON()),
        });
        if (!response.ok) {
            throw new Error(`Push subscription request failed: ${response.status}`);
        }
    }

    private isSupported(): boolean {
        return (
            'serviceWorker' in navigator
            && 'PushManager' in window
            && 'Notification' in window
            && window.isSecureContext
        );
    }

    private showUnsupported(): void {
        this.setStatus('Push notifications are not supported on this browser.');
        this.updateButtons(false);
        this.setToggleDisabled(true);
        if (this.hasUnsupportedTarget) {
            this.unsupportedTarget.classList.remove('hidden');
        }
    }

    private setStatus(message: string): void {
        if (this.hasStatusTarget) {
            this.statusTarget.textContent = message;
        }
    }

    private updateButtons(subscribed: boolean): void {
        this.subscribed = subscribed;
        if (this.hasToggleButtonTarget) {
            this.toggleButtonTarget.setAttribute('aria-checked', String(subscribed));
            this.toggleButtonTarget.classList.toggle('bg-green-600', subscribed);
            this.toggleButtonTarget.classList.toggle('bg-zinc-200', !subscribed);
            this.toggleButtonTarget.disabled = false;
        }
        if (this.hasToggleKnobTarget) {
            this.toggleKnobTarget.classList.toggle('translate-x-5', subscribed);
            this.toggleKnobTarget.classList.toggle('translate-x-0', !subscribed);
        }
        if (this.hasToggleLabelTarget) {
            this.toggleLabelTarget.textContent = subscribed ? 'On' : 'Off';
        }
    }

    private setBusy(isBusy: boolean): void {
        if (this.hasToggleButtonTarget) {
            this.toggleButtonTarget.disabled = isBusy;
        }
    }

    private setToggleDisabled(disabled: boolean): void {
        if (this.hasToggleButtonTarget) {
            this.toggleButtonTarget.disabled = disabled;
        }
    }

    private urlBase64ToUint8Array(base64String: string): ArrayBuffer {
        const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let index = 0; index < rawData.length; index += 1) {
            outputArray[index] = rawData.charCodeAt(index);
        }
        return outputArray.buffer;
    }
}
